import { useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { cn } from "@/lib/utils";

import { fetchApInvoiceMergeCandidates, type ApInvoiceMergeCandidate } from "./apInvoicesApi";

function candidateLabel(row: ApInvoiceMergeCandidate): string {
  const number = row.vendor_invoice_number?.trim();
  return number != null && number !== "" ? number : `Invoice ${row.id}`;
}

export function ApInvoiceMergeModal(props: {
  invoiceId: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (sourceInvoiceId: string) => void;
}): JSX.Element {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const candidatesQuery = useQuery({
    queryKey: ["accounting", "ap-invoice-merge-candidates", props.invoiceId],
    queryFn: () => fetchApInvoiceMergeCandidates(props.invoiceId),
  });
  const rows = candidatesQuery.data ?? [];

  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <Card className="mt-6 w-full max-w-lg shadow-xl">
        <CardHeader className="border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Merge another vendor invoice</h2>
          <p className="mt-1 text-sm text-slate-600">
            Optional. Use this only when these charges belong on one purchase invoice. Leave the invoices separate
            when the vendor sent two bills that should each become their own purchase invoice, even on the same
            purchase order.
          </p>
          <p className="mt-2 text-sm text-slate-600">
            If you merge, lines and documents from the invoice you pick move onto this one. That other invoice is
            kept as merged and will not create its own purchase invoice.
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          {candidatesQuery.isLoading ? (
            <p className="text-sm text-slate-500">Looking for other invoices on this purchase order…</p>
          ) : candidatesQuery.isError ? (
            <p className="text-sm text-rose-700">Could not load invoices to merge.</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-600">
              No other vendor invoices on this purchase order are ready to merge.
            </p>
          ) : (
            <ul className="space-y-2">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    disabled={props.pending}
                    className={cn(
                      "w-full rounded-lg border px-3 py-2 text-left",
                      selectedId === row.id
                        ? "border-sky-400 bg-sky-50"
                        : "border-slate-200 bg-white hover:border-sky-300",
                    )}
                    onClick={() => setSelectedId(row.id)}
                  >
                    <span className="block text-sm font-semibold text-slate-900">{candidateLabel(row)}</span>
                    <span className="block text-xs text-slate-600">
                      {[row.invoice_date, row.total_amount != null ? `${row.total_amount} ${row.currency}` : null]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" disabled={props.pending} onClick={props.onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={props.pending || selectedId == null}
              isLoading={props.pending}
              onClick={() => {
                if (selectedId == null) return;
                props.onConfirm(selectedId);
              }}
            >
              Merge into this invoice
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}
